/**
 * Sidebar panel state for ReasoningChat — dynamic-view / reasoning sidebar
 * open-close, the satisfying-push open animation, and the drag-to-resize
 * behavior. Self-contained: no data from the host component crosses the
 * boundary, only the panel's own state and handlers.
 *
 * The open dynamic view is DERIVED from the `?view=<id>` search param rather
 * than mirrored into local state, and that is the load-bearing decision here.
 * Two copies of one fact need something to keep them in agreement, and the
 * only available something is an effect that reads the param and calls the
 * open handler. That handler necessarily closes over the panel's own open
 * state, so it takes a new identity every time the panel opens or closes —
 * including on close. The effect then re-runs *because the user closed the
 * panel*, finds `?view=` still in the URL, and reopens it: the view could not
 * be closed at all, and the page appeared to loop back to it. Deriving removes
 * the second copy, so closing is a single act (delete the param) with nothing
 * left behind to re-assert the old value. No effect, no sync, no loop.
 *
 * A consequence worth knowing: opening a view now writes the URL, so any open
 * view is directly shareable and Back closes the panel. Closing uses `replace`
 * — dismissing a panel should not be something the user has to undo.
 */

import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const VIEW_PARAM = 'view';

/** Width below which the panel is a full-screen modal, so it must not animate. */
const DESKTOP_MIN_WIDTH = 1024;
const OPEN_ANIMATION_MS = 750;

/**
 * The sidebar is sized as a percentage of the viewport (it shares the row
 * with the chat column). These bound both drag and keyboard resizing and are
 * what the resize handle announces as its range.
 */
export const SIDEBAR_MIN_WIDTH_PERCENT = 25;
export const SIDEBAR_MAX_WIDTH_PERCENT = 60;

function clampSidebarWidth(percent: number): number {
  return Math.min(
    Math.max(percent, SIDEBAR_MIN_WIDTH_PERCENT),
    SIDEBAR_MAX_WIDTH_PERCENT
  );
}

/** Reads the view id the URL is asking for, or null if it asks for nothing valid. */
function parseViewParam(raw: string | null): number | null {
  if (raw === null) {
    return null;
  }
  const viewId = Number(raw);
  return Number.isInteger(viewId) && viewId > 0 ? viewId : null;
}

function withoutViewParam(previous: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(previous);
  next.delete(VIEW_PARAM);
  return next;
}

export interface UseReasoningSidebarPanelResult {
  sidebarDynamicViewId: number | null;
  sidebarChainId: string | null;
  isAnimating: boolean;
  sidebarWidth: number;
  isResizing: boolean;
  handleOpenDynamicViewSidebar: (viewId: number) => void;
  handleOpenReasoningSidebar: (chainId: string) => void;
  handleCloseSidebar: () => void;
  handleResizeStart: (e: ReactMouseEvent) => void;
  /**
   * Adjust the sidebar by a signed PIXEL delta (positive grows), converted to
   * the panel's percentage width and clamped. Shaped for EdgeResizeHandle's
   * onKeyboardResize, which owns the arrow-key → delta mapping.
   */
  resizeSidebarBy: (deltaPx: number) => void;
  /**
   * Closes only the reasoning sidebar. The dynamic view needs no equivalent:
   * it follows the URL, so navigating away from a conversation closes it and
   * arriving at one with `?view=` opens it, both without anyone asking.
   */
  closeReasoningSidebar: () => void;
}

export function useReasoningSidebarPanel(): UseReasoningSidebarPanelResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const sidebarDynamicViewId = parseViewParam(searchParams.get(VIEW_PARAM));

  const [sidebarChainId, setSidebarChainId] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(40); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(40);

  const isPanelOpen = sidebarDynamicViewId !== null || sidebarChainId !== null;

  // The push animation belongs to the closed→open transition, not to any one
  // handler: swapping a view for a chain leaves the panel open throughout and
  // must not replay it. Expressing it as an effect over a single boolean also
  // keeps the handlers below free of panel state, so none of them can drag the
  // panel's own open/closed state into a caller's dependency array — which is
  // how the reopen loop was built. (The handlers are still not referentially
  // stable: `setSearchParams` is `useCallback`-ed on `searchParams`, so it
  // changes whenever the URL does, and opening or closing changes the URL by
  // definition. That is fine, and is not what keeps the bug dead — see above.)
  useEffect(() => {
    if (!isPanelOpen || window.innerWidth < DESKTOP_MIN_WIDTH) {
      return;
    }
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), OPEN_ANIMATION_MS);
    return () => {
      clearTimeout(timer);
      setIsAnimating(false);
    };
  }, [isPanelOpen]);

  const handleOpenDynamicViewSidebar = useCallback(
    (viewId: number) => {
      setSidebarChainId(null);
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        next.set(VIEW_PARAM, String(viewId));
        return next;
      });
    },
    [setSearchParams]
  );

  const handleOpenReasoningSidebar = useCallback(
    (chainId: string) => {
      setSidebarChainId(chainId);
      // The two sidebars are mutually exclusive, so opening this one retracts
      // the URL's request for a dynamic view.
      setSearchParams(withoutViewParam, { replace: true });
    },
    [setSearchParams]
  );

  const handleCloseSidebar = useCallback(() => {
    setSidebarChainId(null);
    setSearchParams(withoutViewParam, { replace: true });
  }, [setSearchParams]);

  const closeReasoningSidebar = useCallback(() => {
    setSidebarChainId(null);
  }, []);

  // Sidebar resize functionality — uses refs for drag state to avoid extra re-renders
  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();

      // Capture initial state in refs (no re-render needed)
      dragStartXRef.current = e.clientX;
      dragStartWidthRef.current = sidebarWidth;
      setIsResizing(true);

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      // Add visual feedback to the entire interface
      document.body.classList.add('is-resizing-sidebar');
    },
    [sidebarWidth]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;

      // Calculate the delta from where drag started (refs avoid stale closure)
      const deltaX = dragStartXRef.current - mouseX;
      const deltaPercent = (deltaX / windowWidth) * 100;

      // Apply delta to the starting width, constrained to the panel's range
      const constrainedWidth = clampSidebarWidth(
        dragStartWidthRef.current + deltaPercent
      );

      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        setSidebarWidth(constrainedWidth);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('is-resizing-sidebar');
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const resizeSidebarBy = useCallback((deltaPx: number) => {
    const deltaPercent = (deltaPx / window.innerWidth) * 100;
    setSidebarWidth(prev => clampSidebarWidth(prev + deltaPercent));
  }, []);

  return useMemo(
    () => ({
      sidebarDynamicViewId,
      sidebarChainId,
      isAnimating,
      sidebarWidth,
      isResizing,
      handleOpenDynamicViewSidebar,
      handleOpenReasoningSidebar,
      handleCloseSidebar,
      handleResizeStart,
      resizeSidebarBy,
      closeReasoningSidebar,
    }),
    [
      sidebarDynamicViewId,
      sidebarChainId,
      isAnimating,
      sidebarWidth,
      isResizing,
      handleOpenDynamicViewSidebar,
      handleOpenReasoningSidebar,
      handleCloseSidebar,
      handleResizeStart,
      resizeSidebarBy,
      closeReasoningSidebar,
    ]
  );
}
